// 判断待ちの案件への回答。回答すると API がエージェントを再開し、その結果（次の質問か完了）を返す。
import { useState } from 'react';
import { ApiError, api, officePath } from '../lib/api';
import { outcomeLabel, parseAgentOutcome, type AgentOutcome } from '../lib/domain';
import { messageOf } from '../lib/errors';
import type { AgentInvokeResult } from '../lib/types';
import { Badge, Button, ErrorText, Textarea } from './ui';

interface AnswerResponse {
  caseId: string;
  generation: number;
  agent?: AgentInvokeResult;
}

export function CaseAnswerForm({
  officeId,
  caseId,
  generation,
  question,
  onDone,
}: {
  officeId: string;
  caseId: string;
  generation: number;
  question: string;
  onDone?: (o: AgentOutcome) => void;
}) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<AgentOutcome | null>(null);

  const submit = async () => {
    if (!text.trim()) return;
    setSending(true);
    setError(null);
    try {
      const r = await api<AnswerResponse>(officePath(officeId, 'cases', caseId, 'answer'), { officeId, body: { generation, answer: { a: text.trim() } } });
      const o = parseAgentOutcome(r.agent);
      setOutcome(o);
      onDone?.(o);
    } catch (e) {
      // 失敗しても入力は消さない
      setError(e instanceof ApiError && e.status === 409 ? 'すでに回答されています' : messageOf(e));
    } finally {
      setSending(false);
    }
  };

  if (outcome) {
    return (
      <div className="space-y-2 rounded-md bg-subtle p-3 text-sm" role="status">
        <div className="flex items-center gap-2">
          <Badge tone={outcome.status === 'done' ? 'confirmed' : outcome.status === 'waiting' ? 'alert' : outcome.status === 'error' || outcome.status === 'not-accepted' ? 'danger' : 'neutral'}>
            {outcome.status === 'done' ? '完了' : outcome.status === 'waiting' ? '判断待ち' : '結果'}
          </Badge>
          <span>{outcomeLabel(outcome)}</span>
        </div>
        {outcome.status === 'waiting' && outcome.question ? <p className="font-medium text-ink-1">次の質問: {outcome.question}</p> : null}
        {outcome.status === 'done' && outcome.text ? <p className="whitespace-pre-wrap text-ink-2">{outcome.text}</p> : null}
      </div>
    );
  }

  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <p className="font-medium text-ink-1">{question || '（質問の文がありません）'}</p>
      <Textarea data-testid="case-answer-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="回答を書いてください" aria-label="回答" disabled={sending} />
      <ErrorText>{error}</ErrorText>
      <div className="flex justify-end">
        <Button data-testid="case-answer-submit" type="submit" disabled={sending || !text.trim()}>
          {sending ? '送信中…' : '回答して再開'}
        </Button>
      </div>
    </form>
  );
}
